import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { CourseState, Course, Enrollment } from '../../types'
import * as courseAPI from '../../api/courses'

const initialState: CourseState = {
  courses: [],
  currentCourse: null,
  enrollments: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchCourses = createAsyncThunk(
  'courses/fetchCourses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await courseAPI.getCourses()
      return response
    } catch (error: any) {
      console.error('❌ fetchCourses: API call failed:', error)
      console.error('❌ fetchCourses: Error response:', error.response)
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch courses')
    }
  }
)

export const fetchCourseById = createAsyncThunk(
  'courses/fetchCourseById',
  async (courseId: string, { rejectWithValue }) => {
    try {
      const response = await courseAPI.getCourseById(courseId)
      return response.data || response // Handle both wrapped and direct responses
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch course')
    }
  }
)

export const fetchUserEnrollments = createAsyncThunk(
  'courses/fetchUserEnrollments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await courseAPI.getUserEnrollments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch enrollments')
    }
  }
)

export const enrollInCourse = createAsyncThunk(
  'courses/enrollInCourse',
  async (courseId: string, { rejectWithValue }) => {
    try {
      const response = await courseAPI.enrollInCourse(courseId)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to enroll in course')
    }
  }
)

export const updateEnrollmentProgress = createAsyncThunk(
  'courses/updateEnrollmentProgress',
  async ({ enrollmentId, progress }: { enrollmentId: string; progress: number }, { rejectWithValue }) => {
    try {
      const response = await courseAPI.updateEnrollmentProgress(enrollmentId, progress)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update progress')
    }
  }
)

const courseSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentCourse: (state, action: PayloadAction<Course | null>) => {
      state.currentCourse = action.payload
    },
    updateCourseProgress: (state, action: PayloadAction<{ courseId: string; progress: number }>) => {
      const enrollment = state.enrollments.find(e => e.courseId === action.payload.courseId)
      if (enrollment) {
        enrollment.progress = action.payload.progress
        if (action.payload.progress >= 100) {
          enrollment.status = 'completed'
          enrollment.completedAt = new Date().toISOString()
        } else if (action.payload.progress > 0) {
          enrollment.status = 'in_progress'
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Courses
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false
        // Handle paginated response - extract results array
        const payload: any = action.payload
        if (payload && payload.results && Array.isArray(payload.results)) {
          state.courses = payload.results
        } else if (Array.isArray(payload)) {
          state.courses = payload
        } else {
          state.courses = []
        }
        state.error = null
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Course By ID
    builder
      .addCase(fetchCourseById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentCourse = action.payload
        state.error = null
      })
      .addCase(fetchCourseById.rejected, (state, action) => {
        console.error('❌ fetchCourseById.rejected:', action.payload)
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch User Enrollments
    builder
      .addCase(fetchUserEnrollments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUserEnrollments.fulfilled, (state, action) => {
        state.isLoading = false
        state.enrollments = action.payload
        state.error = null
      })
      .addCase(fetchUserEnrollments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Enroll in Course
    builder
      .addCase(enrollInCourse.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(enrollInCourse.fulfilled, (state, action) => {
        state.isLoading = false
        state.enrollments.push(action.payload)
        state.error = null
      })
      .addCase(enrollInCourse.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Update Enrollment Progress
    builder
      .addCase(updateEnrollmentProgress.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateEnrollmentProgress.fulfilled, (state, action) => {
        state.isLoading = false
        const updatedEnrollment = action.payload
        const index = state.enrollments.findIndex(e => e.id === updatedEnrollment.id)
        if (index !== -1) {
          state.enrollments[index] = updatedEnrollment
        }
        state.error = null
      })
      .addCase(updateEnrollmentProgress.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setCurrentCourse, updateCourseProgress } = courseSlice.actions
export default courseSlice.reducer
